
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { questions } from "@/components/seller/verification/quizQuestions";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQueryClient } from "@tanstack/react-query";

const CookVerifications = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: verifications, isLoading } = useQuery({
    queryKey: ['cook-verifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cook_verifications')
        .select(`
          *,
          profiles:cook_id (
            first_name,
            last_name,
            city,
            state,
            image_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const handleVerificationUpdate = async (verificationId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      console.log('Updating verification status:', { verificationId, status, rejectionReason });
      
      const { data: verification, error: fetchError } = await supabase
        .from('cook_verifications')
        .select('cook_id')
        .eq('id', verificationId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('cook_verifications')
        .update({ 
          status,
          rejection_reason: rejectionReason,
          verification_date: status === 'approved' ? new Date().toISOString() : null
        })
        .eq('id', verificationId);

      if (error) throw error;

      // After successfully updating the verification, invalidate the query to refresh the data
      await queryClient.invalidateQueries({ queryKey: ['cook-verifications'] });

      toast({
        title: "Success",
        description: `Verification ${status}`,
      });
    } catch (error) {
      console.error('Error updating verification:', error);
      toast({
        title: "Error",
        description: "Failed to update verification status",
        variant: "destructive",
      });
    }
  };

  const calculateProgress = (verification: any) => {
    let completed = 0;
    const total = 5; // Total number of verification steps

    if (verification.government_id_url) completed++;
    if (verification.kitchen_photos?.length) completed++;
    if (verification.food_storage_photos?.length) completed++;
    if (verification.handwashing_photos?.length) completed++;
    if (verification.food_separation_photos?.length) completed++;

    return (completed / total) * 100;
  };

  const renderQuizResults = (verification: any) => {
    if (!verification.quiz_score) return null;
    
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium mb-2">Quiz Results</h4>
        <p>Score: {verification.quiz_score}%</p>
        <p>Completed: {new Date(verification.quiz_completed_at).toLocaleDateString()}</p>
        {verification.quiz_answers && (
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">
              {verification.quiz_answers.reduce((acc: number, answer: number, index: number) => 
                answer === questions[index].correctAnswer ? acc + 1 : acc
              , 0)} correct answers out of {questions.length}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AdminLayout title="Verifications">
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {verifications?.filter(v => v.status === 'pending').map((verification) => (
            <Card key={verification.id} className="p-6">
              <div className="flex flex-col gap-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-4">
                      {verification.profiles?.image_url && (
                        <img 
                          src={verification.profiles.image_url} 
                          alt="Profile" 
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <h3 className="font-medium">
                          {verification.profiles?.first_name} {verification.profiles?.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {verification.profiles?.city}, {verification.profiles?.state}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-x-2">
                    <Button
                      variant="destructive"
                      onClick={() => {
                        const reason = window.prompt('Please provide a reason for rejection:');
                        if (reason) {
                          handleVerificationUpdate(verification.id, 'rejected', reason);
                        }
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      onClick={() => handleVerificationUpdate(verification.id, 'approved')}
                    >
                      Approve
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">Verification Progress</span>
                    <span className="text-sm text-muted-foreground">
                      {Math.round(calculateProgress(verification))}%
                    </span>
                  </div>
                  <Progress value={calculateProgress(verification)} className="h-2" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {verification.government_id_url && (
                    <div>
                      <h4 className="font-medium mb-2">Government ID</h4>
                      <img 
                        src={verification.government_id_url} 
                        alt="Government ID" 
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                    </div>
                  )}
                  {verification.kitchen_photos?.map((photo: string, index: number) => (
                    <div key={index}>
                      <h4 className="font-medium mb-2">Kitchen Photo {index + 1}</h4>
                      <img 
                        src={photo} 
                        alt={`Kitchen ${index + 1}`} 
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                    </div>
                  ))}
                  {verification.food_storage_photos?.map((photo: string, index: number) => (
                    <div key={index}>
                      <h4 className="font-medium mb-2">Food Storage Photo {index + 1}</h4>
                      <img 
                        src={photo} 
                        alt={`Food Storage ${index + 1}`} 
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                    </div>
                  ))}
                  {verification.handwashing_photos?.map((photo: string, index: number) => (
                    <div key={index}>
                      <h4 className="font-medium mb-2">Handwashing Station Photo {index + 1}</h4>
                      <img 
                        src={photo} 
                        alt={`Handwashing ${index + 1}`} 
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                    </div>
                  ))}
                  {verification.food_separation_photos?.map((photo: string, index: number) => (
                    <div key={index}>
                      <h4 className="font-medium mb-2">Food Separation Photo {index + 1}</h4>
                      <img 
                        src={photo} 
                        alt={`Food Separation ${index + 1}`} 
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
              {renderQuizResults(verification)}
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {verifications?.filter(v => v.status === 'approved').map((verification) => (
            <Card key={verification.id} className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-4">
                    {verification.profiles?.image_url && (
                      <img 
                        src={verification.profiles.image_url} 
                        alt="Profile" 
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-medium">
                        {verification.profiles?.first_name} {verification.profiles?.last_name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {verification.profiles?.city}, {verification.profiles?.state}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Approved on: {new Date(verification.verification_date!).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {verifications?.filter(v => v.status === 'rejected').map((verification) => (
            <Card key={verification.id} className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-4">
                    {verification.profiles?.image_url && (
                      <img 
                        src={verification.profiles.image_url} 
                        alt="Profile" 
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-medium">
                        {verification.profiles?.first_name} {verification.profiles?.last_name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {verification.profiles?.city}, {verification.profiles?.state}
                      </p>
                      <p className="text-sm text-destructive mt-2">
                        Reason: {verification.rejection_reason}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default CookVerifications;
